import {
  CoreBatchAction,
  type ID,
  type NodeItem,
  ProjectActions,
  type SectionItem,
  selectSectionsByTab,
} from '@octopus-do/octopus-project-core'
import type { AppThunk } from '@store/migrationActions/thunkActions/types'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import { getCloneId, getId } from '@store/utils'
import { commitProjectRequest } from '@storeOld/actionCreators'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'

import { BASE_COLOR_CLOUDY } from '@/constants/constants'

export const thunkAddEmptySection =
  (): AppThunk => async (dispatch, getState) => {
    const activeTab = migrationProjectSelector.getActiveOrFirstTabId(getState())
    const sections = selectSectionsByTab(activeTab)(getState())

    const sectionIndex = sections.length

    const id = getId()

    const section: SectionItem = {
      id,
      title: 'Section',
      tabId: activeTab,
      colorId: BASE_COLOR_CLOUDY.id,
      order: sectionIndex,
      collapsed: false,
      textSize: 's',
    }

    dispatch(
      ProjectActions.sections.add({
        items: [section],
        index: sectionIndex,
        parents: [activeTab],
      })
    )

    return id
  }

export const thunkAddSection =
  (id: ID): AppThunk =>
  async (dispatch, getState) => {
    const activeTab = migrationProjectSelector.getActiveOrFirstTabId(getState())

    const sections = selectSectionsByTab(activeTab)(getState())
    const sectionIndex = sections.length + 1

    const section: SectionItem = {
      id,
      title: 'Section',
      tabId: activeTab,
      colorId: BASE_COLOR_CLOUDY.id,
      order: sectionIndex,
      collapsed: false,
      textSize: 's',
    }

    const colorId = migrationProjectSelector.getProjectDefaultColorId(
      getState()
    )

    const nodes: NodeItem[] = [
      {
        id: getId(),
        title: 'Page',
        collapsed: false,
        variant: 'default',
        colorId,
        notes: {},
        url: null,
        image: null,
        figma: null,
      },
    ]

    dispatch(
      commitProjectRequest(
        CoreBatchAction({
          type: 'section_creation',
          actions: [
            ProjectActions.sections.add({
              items: [section],
              index: sectionIndex,
              parents: [activeTab],
            }),
            ProjectActions.nodes.add({
              items: nodes,
              index: 0,
              parents: [id],
            }),
          ],
        })
      )
    )
  }

// Копия встаёт сразу под оригиналом. Ядро дописывает клон в конец таба, так что
// после clone его поднимают orderUp по одной позиции до нужного места; всё
// уходит одним коммитом, чтобы undo откатывал дубль целиком.
export const thunkDuplicateSection =
  (id: ID): AppThunk =>
  (dispatch, getState) => {
    const section = migrationProjectSelector.getProjectSectionById(id)(
      getState()
    )

    if (!section) {
      return
    }

    const initiator = getId()
    const clonedId = getCloneId(id, initiator)

    const sectionsInTab = selectSectionsByTab(section.tabId)(getState()).length
    const stepsUp = sectionsInTab - section.order - 1

    dispatch(
      commitProjectRequest(
        ProjectActions.sections.clone({ id, initiator }),
        ...Array.from({ length: stepsUp }, () =>
          ProjectActions.sections.orderUp({ id: clonedId })
        )
      )
    )

    dispatch({
      type: CanvasActionTypes.SELECT_SECTION,
      payload: clonedId,
    })

    dispatch(
      artBoardActions.revealSection({ id: clonedId, skipWhenVisible: true })
    )
  }
